/**
 * 画面上部の共通ヘッダ。
 *
 * 方針（docs/01-requirements.md 指針1・5）: 装飾しない。戻る動線とタイトル、
 * 画面ごとの少数の操作だけを置く。タップ領域は 44px 以上を確保する。
 *
 * **ブランド表示（かつての「豆」）は置かない。** 単独ユーザーのアプリで、開いている本人に
 * アプリ名を知らせる価値がない。左端は「戻る」だけが占める（無い画面は詰める）。
 */

import { useCallback, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@app/components/icons';

export interface AppHeaderProps {
  /** 文字列のほか、ビュー切り替え（`ViewSwitcher`）のような要素も置ける。 */
  title: ReactNode;
  /** 戻り先。数値ならブラウザ履歴を相対移動する。指定すると左端に戻るボタンを出す。 */
  backTo?: string | number;
  /** 文字列の戻り先で、現在の履歴項目を置換する。直リンクのフォールバックに使う。 */
  backReplace?: boolean;
  /** 文字列の戻り先へ引き継ぐルーター state。 */
  backState?: unknown;
  /** 戻る前に実行する処理（編集画面の flush など）。 */
  onBeforeBack?: () => void | Promise<void>;
  /** 右側に置く操作。 */
  right?: ReactNode;
}

export function AppHeader({
  title,
  backTo,
  backReplace = false,
  backState,
  onBeforeBack,
  right,
}: AppHeaderProps) {
  const navigate = useNavigate();

  const handleBack = useCallback(() => {
    void (async () => {
      try {
        await onBeforeBack?.();
      } finally {
        if (typeof backTo === 'number') {
          navigate(backTo);
        } else {
          navigate(backTo ?? '/', { replace: backReplace, state: backState });
        }
      }
    })();
  }, [backReplace, backState, backTo, navigate, onBeforeBack]);

  return (
    <header className="app-header">
      {(backTo || onBeforeBack) && (
        <button type="button" className="icon-button" onClick={handleBack} aria-label="戻る">
          <Icon name="back" />
        </button>
      )}
      {/* 文字列のタイトルは長いと困るので省略記号で切る（`.app-header__title`）。
          要素を渡された場合はその要素が自分でレイアウトを決めるので切らない。
          切ると `ViewSwitcher` のポップアップが overflow に食われて見えなくなる。 */}
      <h1
        className={
          typeof title === 'string' ? 'app-header__title' : 'app-header__title app-header__title--node'
        }
      >
        {title}
      </h1>
      <div className="app-header__actions">{right}</div>
    </header>
  );
}
